// src/components/TextToSpeech.js
import React, { useState, useRef } from "react";
import { Button } from "./ui/button";
import { Alert, AlertDescription } from "./ui/alert";
import { Volume2, Loader2, Square, AlertCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

const TextToSpeech = ({ text }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  const generateSpeech = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch("/api/text-to-speech", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          text: text.slice(0, 4096), // OpenAI has a 4096 character limit
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to generate speech");
      }

      // Create a blob from the audio data
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);

      // Clean up previous audio if it exists
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }

      // Create new audio element
      const audio = new Audio(audioUrl);
      audio.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
      };
      audio.onerror = (e) => {
        setError("Error playing audio");
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
      };

      audioRef.current = audio;
      audio.play();
      setIsPlaying(true);
    } catch (err) {
      console.error("Error generating speech:", err);
      setError(err.message || "Failed to generate speech");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = () => {
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    } else if (!isPlaying) {
      generateSpeech();
    }
  };

  // Cleanup function
  React.useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }
    };
  }, []);

  return (
    <div className="inline-flex items-center">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={handleToggle}
              variant="outline"
              className="w-11 h-11 ml-2 rounded-full p-0"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : isPlaying ? (
                <Square className="h-5 w-5" />
              ) : (
                <Volume2 className="h-5 w-5" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isPlaying ? "Zastaviť" : "Vypočuť si text"}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {error && (
        <Alert variant="destructive" className="absolute mt-2 w-[250px] z-50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <span className="text-sm text-gray-500 ml-2">Generujem zvuk...</span>
      )}
    </div>
  );
};

export default TextToSpeech;
